// ===========================================================================
// Reusable Criteria Checklist component.
//
// Shared by the Underwriting tab (A1) and the analyst tabs, which mount it as
//   <CriteriaChecklist dealId={dealId} lane="A2" />  (and "A3", "A4").
// Adding new props is safe; changing or removing existing props must update
// every consumer.
//
// Props:
//   - dealId: string                     — the owning deal.
//   - lane: "A1" | "A2" | "A3" | "A4"   — which analyst lane this surface
//                                          is rendering. Must remain
//                                          lane-agnostic in the component
//                                          internals; no hard-coded "A1"
//                                          strings inside the component
//                                          (no lint rule checks this).
//   - className?: string                 — pass-through for the outer
//                                          container. Default omitted.
//
// Internal behavior:
//   - Fetches CriteriaCatalogItem rows (cached for 10 minutes) +
//     CriteriaChecklistItem rows scoped to (dealId, lane).
//   - Renders a Tier filter + Status filter as filter chips.
//   - Groups items by tier with subheaders (Deal Killers → Red Flags →
//     Needs Improvement).
//   - Each row delegates to <CriteriaChecklistItemRow>.
//   - Bottom summary: Approved / Declined / Waived / Escalated / Pending counts.
// ===========================================================================

import { useMemo, useState } from "react";
import { Plus } from "lucide-react";
import { Button, Card } from "@/design-system/components/ui";
import { useAuth } from "@/hooks/useAuth";
import {
  useCriteriaCatalog,
  useCriteriaChecklistByLane,
} from "@/hooks/dealFlow/useDealFlow";
import type {
  CriteriaCatalogItemDto,
  CriteriaChecklistItemDto,
  CriteriaLane,
  CriteriaTier,
} from "@/api/dealFlow/types";
import {
  CriteriaChecklistFilters,
  type StatusFilter,
  type TierFilter,
} from "./CriteriaChecklistFilters";
import { CriteriaChecklistItemRow } from "./CriteriaChecklistItem";
import { CriteriaCatalogCreateModal } from "./CriteriaCatalogCreateModal";

const TIER_ORDER: ReadonlyArray<CriteriaTier> = [
  "DealKiller",
  "RedFlag",
  "NeedsImprovement",
];

const TIER_HEADER: Record<CriteriaTier, string> = {
  DealKiller: "Deal Killers",
  RedFlag: "Red Flags",
  NeedsImprovement: "Needs Improvement",
};

export interface CriteriaChecklistProps {
  dealId: string;
  lane: CriteriaLane;
  className?: string;
  /** When true, hides write affordances on every row regardless
   *  of permission. The analyst tabs set it when the deal is Closed; otherwise
   *  each row works out its caller's actions (checklistItemActions). */
  readOnly?: boolean;
}

export function CriteriaChecklist({ dealId, lane, className, readOnly = false }: CriteriaChecklistProps) {
  const auth = useAuth();
  const isAdmin = auth.hasRoleCode("ADMIN");
  const catalogQuery = useCriteriaCatalog();
  const itemsQuery = useCriteriaChecklistByLane(dealId, lane);
  const [tierFilter, setTierFilter] = useState<TierFilter>("All");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("All");
  const [createOpen, setCreateOpen] = useState(false);

  // ADMIN-only "Add criteria" header. Lane is fixed by this surface; Tier is a
  // form field in the modal. Hidden in readOnly mode (mirrors the per-row edit
  // kebab gating). New items appear on every deal via the lazy-create path.
  const adminHeader =
    !readOnly && isAdmin ? (
      <div className="flex items-center justify-end">
        <Button
          variant="secondary"
          size="sm"
          onClick={() => setCreateOpen(true)}
          data-testid="add-criteria-button"
        >
          <Plus size={14} aria-hidden /> Add criteria
        </Button>
      </div>
    ) : null;

  const createModal =
    !readOnly && isAdmin ? (
      <CriteriaCatalogCreateModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        lane={lane}
      />
    ) : null;

  const catalogByItemId = useMemo(() => {
    const map = new Map<string, CriteriaCatalogItemDto>();
    for (const c of catalogQuery.data ?? []) map.set(c.id, c);
    return map;
  }, [catalogQuery.data]);

  const items = itemsQuery.data ?? [];

  const filtered = items.filter(
    (i) =>
      (tierFilter === "All" || i.tier === tierFilter) &&
      (statusFilter === "All" || i.status === statusFilter),
  );

  const groups = TIER_ORDER.map((tier) => ({
    tier,
    items: filtered.filter((i) => i.tier === tier),
  })).filter((g) => g.items.length > 0);

  const counts = countByStatus(items);

  if (catalogQuery.isLoading || itemsQuery.isLoading) {
    return (
      <Card className={className}>
        <div
          className="flex flex-col gap-space-2"
          data-testid="criteria-checklist-skeleton"
        >
          <div className="h-6 bg-white-warm animate-pulse rounded-md" />
          <div className="h-16 bg-white-warm animate-pulse rounded-md" />
          <div className="h-16 bg-white-warm animate-pulse rounded-md" />
          <div className="h-16 bg-white-warm animate-pulse rounded-md" />
        </div>
      </Card>
    );
  }

  if (items.length === 0) {
    return (
      <div
        className={className ?? "flex flex-col gap-space-3"}
        data-testid={`criteria-checklist-${lane}`}
      >
        {adminHeader}
        <Card>
          <p className="text-small text-gray-mid italic m-0">
            No checklist items have been seeded for this deal&apos;s {lane} lane yet.
          </p>
        </Card>
        {createModal}
      </div>
    );
  }

  return (
    <div
      className={className ?? "flex flex-col gap-space-3"}
      data-testid={`criteria-checklist-${lane}`}
    >
      {adminHeader}

      <CriteriaChecklistFilters
        tier={tierFilter}
        status={statusFilter}
        onTierChange={setTierFilter}
        onStatusChange={setStatusFilter}
      />

      {groups.length === 0 && (
        <Card>
          <p className="text-small text-gray-mid italic m-0">
            No items match the current filters.
          </p>
        </Card>
      )}

      {groups.map((g) => (
        <section key={g.tier}>
          <h4 className="font-display text-h4 text-onyx m-0 mb-space-2">
            {TIER_HEADER[g.tier]}{" "}
            <span className="text-caption text-gray-mid font-normal">
              ({g.items.length})
            </span>
          </h4>
          <ul className="flex flex-col gap-space-2 m-0 p-0 list-none">
            {g.items.map((it) => (
              <CriteriaChecklistItemRow
                key={it.id}
                dealId={dealId}
                item={it}
                catalog={catalogByItemId.get(it.catalogItemId)}
                readOnly={readOnly}
              />
            ))}
          </ul>
        </section>
      ))}

      <Card>
        <ProgressSummary counts={counts} total={items.length} />
      </Card>

      {createModal}
    </div>
  );
}

interface StatusCounts {
  approved: number;
  declined: number;
  waived: number;
  escalated: number;
  pending: number;
}

function countByStatus(items: CriteriaChecklistItemDto[]): StatusCounts {
  const c: StatusCounts = {
    approved: 0,
    declined: 0,
    waived: 0,
    escalated: 0,
    pending: 0,
  };
  for (const i of items) {
    switch (i.status) {
      case "Approved":
        c.approved++;
        break;
      case "Declined":
        c.declined++;
        break;
      case "Waived":
        c.waived++;
        break;
      case "Escalated":
        c.escalated++;
        break;
      case "Pending":
        c.pending++;
        break;
    }
  }
  return c;
}

function ProgressSummary({
  counts,
  total,
}: {
  counts: StatusCounts;
  total: number;
}) {
  const closed = counts.approved + counts.declined + counts.waived;
  return (
    <div
      className="flex flex-wrap items-center gap-space-3 text-small text-onyx"
      data-testid="checklist-progress-summary"
    >
      <span className="font-display text-label uppercase tracking-label text-gold-shadow">
        Progress
      </span>
      <span>
        {closed} of {total} closed
      </span>
      <span className="text-gray-mid">·</span>
      <span>{counts.approved} Approved</span>
      <span>{counts.declined} Declined</span>
      <span>{counts.waived} Waived</span>
      <span>{counts.escalated} Escalated</span>
      <span>{counts.pending} Pending</span>
    </div>
  );
}

/** Helper exported so the Lane Pass/Fail section can mirror the
 *  all-items-terminal gate without re-fetching. */
export function isAllItemsTerminal(items: CriteriaChecklistItemDto[]): boolean {
  if (items.length === 0) return false;
  return items.every(
    (i) =>
      i.status === "Approved" || i.status === "Declined" || i.status === "Waived",
  );
}
